/**
 * File: useNotificationsState.ts
 * Path: apps/admin/src/shared/hooks/useNotificationsState.ts
 * Description: Live notification state for the signed-in person: their
 * switches, their devices and their bell.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  NOTIFICATION_EVENTS,
  NOTIFICATION_EVENT_IDS,
  type NotificationEvent,
  type NotificationItem,
  type NotificationSettings,
  type PushDevice,
} from "@idk/config";
import { toast } from "@idk/ui";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { useRole } from "@/modules/auth/hooks/useRoleQueries";
import { notificationsRepo } from "../services/notificationsService";

interface Live<T> {
  data: T;
  loading: boolean;
  error: Error | null;
}

/** Subscribes while signed in; `watch` returns its unsubscribe. */
const useLive = <T,>(
  initial: T,
  watch: (uid: string, next: (v: T) => void, fail: (e: Error) => void) => () => void,
): Live<T> => {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const [state, setState] = useState<Live<T> & { uid: string | null }>({
    uid: null,
    data: initial,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!uid) return undefined;
    return watch(
      uid,
      (data) => setState({ uid, data, loading: false, error: null }),
      (error) => setState((s) => ({ ...s, uid, loading: false, error })),
    );
  }, [uid, watch]);

  // Until the first answer for this person arrives.
  if (state.uid !== uid) return { data: initial, loading: true, error: null };
  return { data: state.data, loading: state.loading, error: state.error };
};

const complain = (action: string) => (error: unknown) =>
  toast.error(error instanceof Error ? error.message : `Couldn't ${action}.`);

/** The events this person's role can receive, in catalog order (the test is never listed). */
export const useAvailableEvents = (): NotificationEvent[] => {
  const { can } = useRole();
  return useMemo(
    () =>
      NOTIFICATION_EVENT_IDS.filter((id) => {
        const info = NOTIFICATION_EVENTS[id];
        if ("hidden" in info && info.hidden) return false;
        return info.permission === null || can(info.permission);
      }),
    [can],
  );
};

export const useNotificationSettings = () => {
  const { user } = useAuth();
  const live = useLive<NotificationSettings | null>(null, notificationsRepo.watchSettings);
  const setEvent = useCallback(
    (event: NotificationEvent, on: boolean) => {
      if (user) notificationsRepo.setEvent(user.uid, event, on).catch(complain("save that"));
    },
    [user],
  );
  const setEnabled = useCallback(
    (enabled: boolean) => {
      if (user) notificationsRepo.setEnabled(user.uid, enabled).catch(complain("save that"));
    },
    [user],
  );
  return { ...live, setEvent, setEnabled };
};

export const usePushDevices = () => {
  const { user } = useAuth();
  const live = useLive<PushDevice[]>([], notificationsRepo.watchDevices);
  const remove = useCallback(
    (deviceId: string) =>
      user ? notificationsRepo.removeDevice(user.uid, deviceId) : Promise.resolve(),
    [user],
  );
  return { ...live, remove };
};

export const useInbox = () => {
  const { user } = useAuth();
  const live = useLive<NotificationItem[]>([], notificationsRepo.watchInbox);
  const unread = useMemo(() => live.data.filter((i) => i.readAt === null).length, [live.data]);
  const markRead = useCallback(
    (ids: string[]) => {
      if (user) notificationsRepo.markRead(user.uid, ids).catch(complain("mark that read"));
    },
    [user],
  );
  const markAllRead = useCallback(
    () => markRead(live.data.filter((i) => i.readAt === null).map((i) => i.id)),
    [live.data, markRead],
  );
  return { ...live, unread, markRead, markAllRead };
};
